import { Dispatch, FC, memo, SetStateAction, useCallback, useMemo, useState } from 'react'
import { useField, UseFieldConfig, useForm } from 'react-final-form'
import { IInputProps } from 'components/extend/Input'
import ModalItem from 'components/ModalItem'
import { MODAL_IDS, useModal } from 'components/ModalProvider'
import { IInputPropsGenericExtend } from 'components/ui/InputBase'
import { FieldValidator } from 'final-form'
import { checkFieldError, getFieldErrorMessage } from 'helpers/form'
import { useActions, useOnUnmount } from 'hooks'
import { emptyCustomer } from 'modules/order/mocks'
import { TSelectCustomer } from 'modules/order/types'
import { TOrderCreateCustomer, TOrderCreateFormData } from 'modules/order/types/orderCreateTypes'
import { CustomerItem, OrderItemClient } from 'schema'
import CustomerAddModal from 'src/modules/customer/components/CustomerAddModal/CustomerAddModal'
import { CustomerAddLocationKeys } from 'store/customerAdd/constants'

import { OrderCreateCustomer } from '../components/OrderCreate/OrderCreateCustomer/OrderCreateCustomer'
import { useOrderCreateFormData } from '../pages/OrderCreateRoute/OrderCreateProvider'

interface FieldOrderCreateCustomerProps
  extends Pick<UseFieldConfig<CustomerItem.type>, 'validate'>,
    Omit<IInputProps<IInputPropsGenericExtend>, 'name' | 'value' | 'defaultValue'> {
  customerTypeValue: OrderItemClient.type
  name: string
  setIsContactsByCustomerList: Dispatch<SetStateAction<boolean>>
  setIsActiveCustomerAddContactAccess?: Dispatch<SetStateAction<boolean>>
}

const STORE_KEY = CustomerAddLocationKeys.OrderAddCustomer

const FieldOrderCreateCustomer: FC<FieldOrderCreateCustomerProps> = (props) => {
  const { name, validate, customerTypeValue, setIsContactsByCustomerList, setIsActiveCustomerAddContactAccess } = props
  const { openModal, closeModal } = useModal()
  const { setCustomerId } = useActions()
  const { setActiveCustomerId, setActiveCustomer, selectedCustomer, setSelectedCustomer } = useOrderCreateFormData()
  const [isShowSelect, setIsShowSelect] = useState(false)
  const form = useForm<TOrderCreateFormData>()

  const handleClearErrors = useCallback(() => {
    form.reset()
  }, [form])

  const handleValidate: FieldValidator<CustomerItem.type> = (value, allValues, meta) => {
    // @ts-ignore
    const customerType = allValues.customer?.type

    return customerType === OrderItemClient.type.LEGAL && validate ? validate(value, allValues, meta) : undefined
  }

  const { input: customerInput, meta: customerMeta } = useField<TOrderCreateCustomer>(name)
  const { input: customerHasSpecialCargoLimitsInput } = useField<TOrderCreateCustomer['hasSpecialCargoLimits']>(
    `${name}.hasSpecialCargoLimits`
  )
  const { input: customerIdInput, meta: customerIdMeta } = useField<CustomerItem.type>(`${name}.customerId`, {
    validate: handleValidate
  })

  const error = useMemo(() => getFieldErrorMessage(checkFieldError(customerIdMeta)), [customerIdMeta])

  const handleSelectCustomer = useCallback(
    (customer: TSelectCustomer) => {
      customer?.id && setCustomerId(customer.id)
      setSelectedCustomer(customer?.id ? customer : undefined)
      setIsShowSelect(false)
    },
    [setCustomerId, setSelectedCustomer]
  )

  const handleOpenCustomerAddModal = useCallback(() => {
    openModal(MODAL_IDS.AddCustomerOrderCreateCustomer)
  }, [openModal])

  const handleChange = useCallback(
    (customer?: CustomerItem) => {
      const customerId = customerMeta.initial?.customerId
      const company = customerMeta.initial?.company
      const type = customerMeta.initial?.type
      const hasSpecialCargoLimits = !!customerMeta.initial?.hasSpecialCargoLimits

      if (!customer?.id && type && customerId) {
        customerInput.onChange({
          customerId,
          type,
          hasSpecialCargoLimits
        })
        setSelectedCustomer({
          id: customerId,
          type,
          name: company?.name || '',
          inn: company?.inn || '',
          kpp: company?.kpp || '',
          hasSpecialCargoLimits
        })
        setCustomerId(customerId)

        return
      }

      customerHasSpecialCargoLimitsInput.onChange(!!customer?.hasSpecialCargoLimits)

      if (customer?.type === CustomerItem.type.LEGAL && customer?.id) {
        customerIdInput.onChange(customer?.id)
      } else {
        customerIdInput.onChange(undefined)
      }

      if (customer) {
        setSelectedCustomer(customer as TSelectCustomer)
      }
    },
    [
      customerHasSpecialCargoLimitsInput,
      customerIdInput,
      customerInput,
      customerMeta,
      setCustomerId,
      setSelectedCustomer
    ]
  )

  useOnUnmount(() => setSelectedCustomer(undefined))

  return (
    <div data-name={name}>
      <ModalItem id={MODAL_IDS.AddCustomerOrderCreateCustomer}>
        <CustomerAddModal
          handleSelectValue={handleSelectCustomer}
          onClose={() => closeModal(MODAL_IDS.AddCustomerOrderCreateCustomer)}
          setActiveCustomer={setActiveCustomer}
          setActiveCustomerId={setActiveCustomerId}
          setIsActiveCustomerAddContactAccess={setIsActiveCustomerAddContactAccess}
          setIsContactsByCustomerList={setIsContactsByCustomerList}
          storeKey={STORE_KEY}
        />
      </ModalItem>

      <OrderCreateCustomer
        clearErrors={handleClearErrors}
        customerTypeValue={customerTypeValue}
        emptyCustomer={emptyCustomer}
        error={error}
        handleOpenCustomerAddModal={handleOpenCustomerAddModal}
        isShowSelect={isShowSelect}
        onChange={handleChange}
        selectedCustomer={selectedCustomer}
        setCustomerId={setCustomerId}
        setIsShowSelect={setIsShowSelect}
        setSelectedCustomer={handleSelectCustomer}
      />
    </div>
  )
}

export default memo(FieldOrderCreateCustomer)
